import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { UserSummaryType } from '../../../../../../../api/types/user/user-summary.type';
import { Checkbox } from 'primereact/checkbox';
import { AttendanceType } from '../../../../../../../api/types/lecturers/attendance/attendance-type';
import React, { useCallback, useMemo, useState } from 'react';
import { UserAttendanceRequest } from '../../../../../../../api/types/lecturers/attendance/user-attendance-request';
import { LectureAttendanceSessionWithAttendanceDB } from '../../../../../../../api/types/lecturers/details/lecture-details';

export interface AttendanceSessionPayload {
  sessionId: number;
  attendance: UserAttendanceRequest[];
}

interface AttendanceDataFormModel {
  sessions: LectureAttendanceSessionWithAttendanceDB[];
  students: UserSummaryType[];
  handleCompleteLecture: (
    sessions: AttendanceSessionPayload[],
  ) => void | Promise<void>;
  /** When 'full', submits all students for every session. When 'delta' (default), only changed rows. */
  submitMode?: 'delta' | 'full';
  isSaving?: boolean;
  onCancel?: () => void;
  approveLabel?: string;
}

type AttendanceMatrix = Record<number, Record<number, AttendanceType>>;

const STATUS_OPTIONS: { type: AttendanceType; label: string }[] = [
  { type: AttendanceType.Attended, label: 'נוכח' },
  { type: AttendanceType.Late, label: 'איחור' },
  { type: AttendanceType.Missed, label: 'חיסור' },
];

const SESSION_COLUMN_MIN_WIDTH = 180;

const cellRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '8px',
  width: '100%',
};

const cellStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px',
  flex: 1,
};

const buildInitialMatrix = (
  sessions: LectureAttendanceSessionWithAttendanceDB[],
  students: UserSummaryType[],
): AttendanceMatrix => {
  const matrix: AttendanceMatrix = {};
  sessions.forEach((sessionEntry) => {
    const sessionId = sessionEntry.session.id;
    const perSession: Record<number, AttendanceType> = {};
    const existingByStudentId = new Map(
      sessionEntry.attendance.map((row) => [
        row.student.id,
        row.attendance?.attendanceType,
      ]),
    );
    students.forEach((student) => {
      const existing = existingByStudentId.get(student.id);
      perSession[student.id] = existing ?? AttendanceType.Attended;
    });
    matrix[sessionId] = perSession;
  });
  return matrix;
};

const cloneMatrix = (matrix: AttendanceMatrix): AttendanceMatrix => {
  const next: AttendanceMatrix = {};
  Object.entries(matrix).forEach(([sessionId, perSession]) => {
    next[Number(sessionId)] = { ...perSession };
  });
  return next;
};

export const AttendanceDataForm: React.FC<AttendanceDataFormModel> = ({
  sessions,
  students,
  handleCompleteLecture,
  submitMode = 'delta',
  isSaving = false,
  onCancel,
  approveLabel = 'אישור',
}) => {
  const initialMatrix = useMemo(
    () => buildInitialMatrix(sessions, students),
    [sessions, students],
  );

  const [attendanceMatrix, setAttendanceMatrix] =
    useState<AttendanceMatrix>(initialMatrix);
  const [internalSaving, setInternalSaving] = useState(false);
  const effectiveSaving = isSaving || internalSaving;

  const handleAttendanceChange = (
    sessionId: number,
    studentId: number,
    status: AttendanceType,
    checked: boolean,
  ) => {
    setAttendanceMatrix((prev) => {
      const next = cloneMatrix(prev);
      if (!next[sessionId]) {
        next[sessionId] = {};
      }
      next[sessionId][studentId] = checked ? status : AttendanceType.Missed;
      return next;
    });
  };

  const handleSessionColumnChange = (
    sessionId: number,
    status: AttendanceType,
    checked: boolean,
  ) => {
    setAttendanceMatrix((prev) => {
      const next = cloneMatrix(prev);
      const perSession = next[sessionId] ? { ...next[sessionId] } : {};
      students.forEach((student) => {
        perSession[student.id] = checked ? status : AttendanceType.Missed;
      });
      next[sessionId] = perSession;
      return next;
    });
  };

  const areAllStudentsMarkedForSession = useCallback(
    (sessionId: number, status: AttendanceType): boolean => {
      if (students.length === 0) return false;
      const perSession = attendanceMatrix[sessionId];
      if (!perSession) return false;
      return students.every((student) => perSession[student.id] === status);
    },
    [attendanceMatrix, students],
  );

  const areSomeStudentsMarkedForSession = useCallback(
    (sessionId: number, status: AttendanceType): boolean => {
      const perSession = attendanceMatrix[sessionId];
      if (!perSession) return false;
      const some = students.some(
        (student) => perSession[student.id] === status,
      );
      return some && !areAllStudentsMarkedForSession(sessionId, status);
    },
    [attendanceMatrix, students, areAllStudentsMarkedForSession],
  );

  const computeDelta = useCallback((): AttendanceSessionPayload[] => {
    const delta: AttendanceSessionPayload[] = [];
    sessions.forEach((sessionEntry) => {
      const sessionId = sessionEntry.session.id;
      const currentPerSession = attendanceMatrix[sessionId] ?? {};
      const initialPerSession = initialMatrix[sessionId] ?? {};

      const changed: UserAttendanceRequest[] = [];
      students.forEach((student) => {
        const current = currentPerSession[student.id];
        const initial = initialPerSession[student.id];
        if (current !== initial) {
          changed.push({
            studentId: student.id,
            attendance: current,
          });
        }
      });

      if (changed.length > 0) {
        delta.push({
          sessionId: sessionId,
          attendance: changed,
        });
      }
    });
    return delta;
  }, [sessions, students, attendanceMatrix, initialMatrix]);

  const computeFull = useCallback((): AttendanceSessionPayload[] => {
    return sessions.map((sessionEntry) => {
      const sessionId = sessionEntry.session.id;
      const currentPerSession = attendanceMatrix[sessionId] ?? {};

      return {
        sessionId: sessionId,
        attendance: students.map((student) => ({
          studentId: student.id,
          attendance: currentPerSession[student.id] ?? AttendanceType.Attended,
        })),
      };
    });
  }, [sessions, students, attendanceMatrix]);

  const sessionHeader = (
    sessionEntry: LectureAttendanceSessionWithAttendanceDB,
  ) => {
    const { session } = sessionEntry;
    const title =
      session.sessionName && session.sessionName.trim().length > 0
        ? session.sessionName
        : `מפגש ${session.sessionNumber}`;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
          minWidth: `${SESSION_COLUMN_MIN_WIDTH}px`,
          width: '100%',
        }}
      >
        <span style={{ fontWeight: 600 }}>{title}</span>
        <div style={cellRowStyle}>
          {STATUS_OPTIONS.map((opt) => (
            <div key={`hdr-${session.id}-${opt.type}`} style={cellStyle}>
              <span style={{ fontSize: '12px', color: '#4b5563' }}>
                {opt.label}
              </span>
              <Checkbox
                checked={areAllStudentsMarkedForSession(session.id, opt.type)}
                onChange={(e) =>
                  handleSessionColumnChange(
                    session.id,
                    opt.type,
                    Boolean(e.checked),
                  )
                }
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const sessionBody = (
    sessionEntry: LectureAttendanceSessionWithAttendanceDB,
    student: UserSummaryType,
  ) => {
    const sessionId = sessionEntry.session.id;
    const current = attendanceMatrix[sessionId]?.[student.id];

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6pc',
          minWidth: `${SESSION_COLUMN_MIN_WIDTH}px`,
          width: '100%',
          maxWidth: '180px',
        }}
      >
        <div style={cellRowStyle}>
          {STATUS_OPTIONS.map((opt) => (
            <div
              key={`cell-${sessionId}-${student.id}-${opt.type}`}
              style={cellStyle}
            >
              <Checkbox
                checked={current === opt.type}
                onChange={(e) =>
                  handleAttendanceChange(
                    sessionId,
                    student.id,
                    opt.type,
                    Boolean(e.checked),
                  )
                }
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleResetToInitial = () => {
    setAttendanceMatrix(initialMatrix);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    handleResetToInitial();
  };

  const handleApprove = async () => {
    const payload = submitMode === 'full' ? computeFull() : computeDelta();

    try {
      const result = handleCompleteLecture(payload);
      if (result && typeof (result as Promise<void>).then === 'function') {
        setInternalSaving(true);
        await result;
      }
    } finally {
      setInternalSaving(false);
    }
  };

  return (
    <div>
      <DataTable
        key={JSON.stringify(attendanceMatrix)}
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        value={students}
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
      >
        <Column
          style={{ textAlign: 'right', width: '50px' }}
          header="מס״ד"
          body={(student: UserSummaryType, options: ColumnBodyOptions) => (
            <label>{options.rowIndex + 1}</label>
          )}
        />
        <Column
          style={{ textAlign: 'right' }}
          header="שם הסטודנט"
          body={(student: UserSummaryType) => (
            <div>
              {student.firstName} {student.lastName}
            </div>
          )}
        />

        <Column
          style={{ textAlign: 'right' }}
          header="ת.ז"
          body={(student: UserSummaryType) => <div>{student.nationalId}</div>}
        />

        {sessions.map((sessionEntry) => (
          <Column
            key={`col-${sessionEntry.session.id}`}
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            header={sessionHeader(sessionEntry)}
            body={(student: UserSummaryType) =>
              sessionBody(sessionEntry, student)
            }
          />
        ))}
      </DataTable>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'left',
          marginTop: '8px',
          paddingBottom: '10px',
        }}
      >
        <Button
          style={{ height: '30px' }}
          severity={'secondary'}
          outlined={true}
          label={'ביטול'}
          onClick={handleCancel}
          disabled={effectiveSaving}
        />

        <div style={{ width: '10px' }} />
        <Button
          style={{ height: '30px' }}
          label={approveLabel}
          onClick={handleApprove}
          loading={effectiveSaving}
          disabled={effectiveSaving}
        />
      </div>
    </div>
  );
};
